import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Tag } from '@/components/ui/tag';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { ExternalLink, Loader2, ChefHat, UtensilsCrossed, Award, Plus, Minus, Info, X, Clock } from 'lucide-react';
import {
  getCustomizedRecipeDetails,
  getCustomizedRecipeByItem,
  updateNutritionServings,
  updateFinalizedMealPlanItem,
  updateManualMealPlanServings,
} from '@/lib/apiService';
import { RecipeDetails } from '@/types/recipe-details';
import { extractErrorMessage } from '@/utils/toast-utils';
import { MacroDonutWithLegend } from '@/components/shared/MacroDonutWithLegend';
import InlineFeedback from '@/components/shared/InlineFeedback';
import { convertMacroQuantityToGrams } from '@/utils/nutrition-units';
import { roundNutritionDisplay } from '@/utils/nutrition-display-format';
import IngredientsTab from './IngredientsTab';
import InstructionsTab from './InstructionsTab';
import NutritionTab from './NutritionTab';
import { getDietaryDisplayName } from '@/data/dietary-mapping';
import { DEFAULT_RECIPE_IMAGE } from '@/constants/media';
import type { RecipeCalorieCalculation, RecipeNutrition, NutrientValue, RecipeIngredient } from '@/types/recipe-details';
import type { RecipeServingSize } from '@/types/custom-recipe';

const DEFAULT_NUTRITION_UNITS: Record<string, string> = {
  calories: 'kcal',
  protein: 'g',
  carbohydrate: 'g',
  total_fat: 'g',
  dietary_fibre: 'g',
  sugar: 'g',
  sodium: 'mg',
  cholesterol: 'mg',
  saturated_fat: 'g',
  trans_fat: 'g',
  mufa: 'g',
  pufa: 'g',
  vitamin_a: 'mcg RAE',
  vitamin_c: 'mg',
  vitamin_d: 'mcg',
  vitamin_e: 'mg',
  vitamin_k: 'mcg',
  vitamin_b1: 'mg',
  vitamin_b2: 'mg',
  vitamin_b3: 'mg',
  vitamin_b6: 'mg',
  vitamin_b9: 'mcg DFE',
  vitamin_b12: 'mcg',
  iron: 'mg',
  zinc: 'mg',
  calcium: 'mg',
  magnesium: 'mg',
  phosphorus: 'mg',
  potassium: 'mg',
};

const num = (o: Record<string, unknown>, k: string): number => (o[k] != null ? Number(o[k]) : 0);
const asRecord = (value: unknown): Record<string, unknown> | undefined =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;

const resolveNutritionUnit = (
  nutritionUnits: Record<string, unknown> | undefined,
  key: string,
  fallbackUnit: string,
): string => {
  const explicitUnit = nutritionUnits?.[key];
  if (typeof explicitUnit === 'string' && explicitUnit.trim().length > 0) {
    return explicitUnit.trim();
  }
  return DEFAULT_NUTRITION_UNITS[key] ?? fallbackUnit;
};

const nutrient = (
  flat: Record<string, unknown>,
  nutritionUnits: Record<string, unknown> | undefined,
  key: string,
  fallbackUnit: string,
): NutrientValue => ({
  quantity: num(flat, key),
  unit: resolveNutritionUnit(nutritionUnits, key, fallbackUnit),
});

function getReadyInMinutes(details: RecipeDetails | null | undefined): number {
  if (!details) return 0;
  const total = Number(details.totalTimeMinutes);
  if (Number.isFinite(total) && total > 0) return Math.round(total);
  const ready = Number(details.readyInMinutes);
  if (Number.isFinite(ready) && ready > 0) return Math.round(ready);
  const prep = Number(details.prepTimeMinutes) > 0 ? Number(details.prepTimeMinutes) : 0;
  const cook = Number(details.cookTimeMinutes) > 0 ? Number(details.cookTimeMinutes) : 0;
  const sum = prep + cook;
  return Number.isFinite(sum) && sum > 0 ? Math.round(sum) : 0;
}

function getDisplayCaloriesKcal(details: RecipeDetails | null | undefined): number | null {
  if (!details) return null;
  const n = details.nutritionDetails || details.nutrition;
  const q = n?.calories?.quantity;
  if (typeof q === 'number' && Number.isFinite(q)) return q;
  const parsed = Number.parseFloat(String(details.caloriesPerServing ?? '').replace(/[^\d.]/g, ''));
  return Number.isFinite(parsed) ? parsed : null;
}

function formatConsumptionChip(servings: number): string {
  const rounded = Math.round(servings * 10) / 10;
  const n = Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
  return `${n} ${rounded === 1 ? 'serving' : 'servings'}`;
}

function hasMacroDistributionNutrition(
  nutrition: RecipeNutrition | null | undefined,
): nutrition is RecipeNutrition {
  const macros = nutrition?.macros;
  return Boolean(nutrition?.calories && macros?.protein && macros?.carbs && macros?.fat);
}

const areNutritionValuesEqual = (
  current: RecipeNutrition | null | undefined,
  original: RecipeNutrition | null | undefined,
): boolean => {
  if (!hasMacroDistributionNutrition(current) || !hasMacroDistributionNutrition(original)) {
    return false;
  }

  const valuesMatch = (a: number, b: number): boolean => Math.abs(a - b) < 0.005;
  const macroGramsMatch = (a: NutrientValue, b: NutrientValue): boolean =>
    valuesMatch(
      convertMacroQuantityToGrams(a.quantity, a.unit),
      convertMacroQuantityToGrams(b.quantity, b.unit),
    );

  return (
    valuesMatch(current.calories.quantity, original.calories.quantity) &&
    macroGramsMatch(current.macros.carbs, original.macros.carbs) &&
    macroGramsMatch(current.macros.protein, original.macros.protein) &&
    macroGramsMatch(current.macros.fat, original.macros.fat)
  );
};

function flatNutritionToRecipeNutrition(
  flat: Record<string, unknown>,
  nutritionUnits?: Record<string, unknown>,
): RecipeNutrition {
  return {
    calories: nutrient(flat, nutritionUnits, 'calories', 'kcal'),
    macros: {
      protein: nutrient(flat, nutritionUnits, 'protein', 'g'),
      carbs: nutrient(flat, nutritionUnits, 'carbohydrate', 'g'),
      fat: nutrient(flat, nutritionUnits, 'total_fat', 'g'),
      fiber: nutrient(flat, nutritionUnits, 'dietary_fibre', 'g'),
      sugar: nutrient(flat, nutritionUnits, 'sugar', 'g'),
      sodium: nutrient(flat, nutritionUnits, 'sodium', 'mg'),
      cholesterol: nutrient(flat, nutritionUnits, 'cholesterol', 'mg'),
      saturatedFat: nutrient(flat, nutritionUnits, 'saturated_fat', 'g'),
      transFat: nutrient(flat, nutritionUnits, 'trans_fat', 'g'),
      monounsaturatedFat: nutrient(flat, nutritionUnits, 'mufa', 'g'),
      polyunsaturatedFat: nutrient(flat, nutritionUnits, 'pufa', 'g'),
    },
    micros: {
      vitamins: {
        vitaminA: nutrient(flat, nutritionUnits, 'vitamin_a', 'mcg RAE'),
        vitaminC: nutrient(flat, nutritionUnits, 'vitamin_c', 'mg'),
        vitaminD: nutrient(flat, nutritionUnits, 'vitamin_d', 'mcg'),
        vitaminE: nutrient(flat, nutritionUnits, 'vitamin_e', 'mg'),
        vitaminK: nutrient(flat, nutritionUnits, 'vitamin_k', 'mcg'),
        thiamin: nutrient(flat, nutritionUnits, 'vitamin_b1', 'mg'),
        riboflavin: nutrient(flat, nutritionUnits, 'vitamin_b2', 'mg'),
        niacin: nutrient(flat, nutritionUnits, 'vitamin_b3', 'mg'),
        vitaminB6: nutrient(flat, nutritionUnits, 'vitamin_b6', 'mg'),
        folate: nutrient(flat, nutritionUnits, 'vitamin_b9', 'mcg DFE'),
        vitaminB12: nutrient(flat, nutritionUnits, 'vitamin_b12', 'mcg'),
      },
      minerals: {
        iron: nutrient(flat, nutritionUnits, 'iron', 'mg'),
        zinc: nutrient(flat, nutritionUnits, 'zinc', 'mg'),
        calcium: nutrient(flat, nutritionUnits, 'calcium', 'mg'),
        magnesium: nutrient(flat, nutritionUnits, 'magnesium', 'mg'),
        phosphorus: nutrient(flat, nutritionUnits, 'phosphorus', 'mg'),
        potassium: nutrient(flat, nutritionUnits, 'potassium', 'mg'),
      },
    },
  };
}

const resolvePreferredNutritionDetails = (payload: Record<string, unknown>): RecipeNutrition | undefined => {
  const customization = payload.customization as Record<string, unknown> | undefined;
  const customizationNutrition = asRecord(customization?.nutrition);
  const customizationNutritionUnits = asRecord(customization?.nutritionUnits);
  if (customizationNutrition) {
    return flatNutritionToRecipeNutrition(customizationNutrition, customizationNutritionUnits);
  }

  const topLevelNutrition = asRecord(payload.nutrition);
  const topLevelNutritionUnits = asRecord(payload.nutritionUnits);
  if (topLevelNutrition) {
    return flatNutritionToRecipeNutrition(topLevelNutrition, topLevelNutritionUnits);
  }

  return undefined;
};

/**
 * Normalize API recipe response to RecipeDetails shape.
 * Extracts ingredients from customization.finalIngredients and converts flat nutrition to structured format.
 */
function normalizeCustomizedRecipeResponse(data: unknown): RecipeDetails {
  const d = data as Record<string, unknown> | null;
  const fallback = { ingredients: [], nutritionDetails: null } as unknown as RecipeDetails;
  if (!d) return fallback;

  const custom = d.customization as Record<string, unknown> | undefined;
  const recipe = d.recipe as Record<string, unknown> | undefined;
  const normalizeServingId = (value: unknown): string | null =>
    typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;
  const toPositiveNumber = (value: unknown): number | undefined => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric) || numeric <= 0) return undefined;
    return Math.round(numeric * 100) / 100;
  };
  const normalizeServingSizes = (value: unknown) => {
    if (!Array.isArray(value)) return undefined;
    const normalized = value
      .map((entry) => {
        const record = asRecord(entry);
        if (!record) return null;
        const servingId = normalizeServingId(record.servingId);
        const grams = toPositiveNumber(record.grams);
        if (!servingId || grams == null) return null;
        return {
          servingId,
          grams,
          label: typeof record.label === 'string' && record.label.trim().length > 0
            ? record.label.trim()
            : undefined,
          isDefault: Boolean(record.isDefault),
        };
      })
      .filter((entry): entry is NonNullable<typeof entry> => entry !== null);
    return normalized.length > 0 ? normalized : undefined;
  };

  const pickPositiveInt = (...values: unknown[]): number | undefined => {
    for (const value of values) {
      const numeric = Number(value);
      if (Number.isFinite(numeric) && numeric > 0) {
        return Math.round(numeric);
      }
    }
    return undefined;
  };

  const prepTimeMinutes = pickPositiveInt(
    d.prepTimeMinutes,
    d.prepMinutes,
    recipe?.prepTimeMinutes,
    recipe?.prepMinutes,
  );
  const cookTimeMinutes = pickPositiveInt(
    d.cookTimeMinutes,
    d.cookMinutes,
    recipe?.cookTimeMinutes,
    recipe?.cookMinutes,
  );
  const totalTimeMinutes = pickPositiveInt(
    d.totalTimeMinutes,
    d.totalMinutes,
    d.readyInMinutes,
    recipe?.totalTimeMinutes,
    recipe?.totalMinutes,
    recipe?.readyInMinutes,
  ) ?? (
    (prepTimeMinutes ?? 0) + (cookTimeMinutes ?? 0) > 0
      ? (prepTimeMinutes ?? 0) + (cookTimeMinutes ?? 0)
      : undefined
  );
  const readyInMinutes = pickPositiveInt(
    d.readyInMinutes,
    recipe?.readyInMinutes,
    totalTimeMinutes,
  );

  // Prefer recalculated nutrition after ingredient changes (custom.nutrition),
  // then the top-level nutrition which usually reflects the current state in a draft/customized response (d.nutrition).
  // Original nutrition is kept separately so finalized items can compare current consumption values with the base recipe.
  const currentNutritionCandidates = [
    {
      values: asRecord(custom?.nutrition),
      units: asRecord(custom?.nutritionUnits),
    },
    {
      values: asRecord(d.nutrition),
      units: asRecord(d.nutritionUnits),
    },
  ];
  const originalNutritionCandidates = [
    {
      values: asRecord(custom?.originalNutrition),
      units: asRecord(custom?.originalNutritionUnits) ?? asRecord(recipe?.nutritionUnits),
    },
    {
      values: asRecord(d.originalNutrition),
      units: asRecord(d.originalNutritionUnits) ?? asRecord(d.nutritionUnits),
    },
    {
      values: asRecord(d.nutritionPerServing),
      units: asRecord(d.nutritionUnits),
    },
    {
      values: asRecord(recipe?.nutritionPerServing),
      units: asRecord(recipe?.nutritionUnits),
    },
  ];
  const selectedCurrentNutrition = currentNutritionCandidates.find((candidate) => candidate.values);
  const selectedOriginalNutrition = originalNutritionCandidates.find((candidate) => candidate.values);
  const selectedNutrition = selectedCurrentNutrition ?? selectedOriginalNutrition;
  const flatNut = selectedNutrition?.values;
  const nutritionUnits = selectedNutrition?.units;
  const originalFlatNut = selectedCurrentNutrition ? selectedOriginalNutrition?.values : undefined;
  const originalNutritionUnits = selectedCurrentNutrition ? selectedOriginalNutrition?.units : undefined;

  // Prefer finalIngredients (updated after modifications); fallback to ingredients then recipe list
  const customIngredients = (custom?.finalIngredients ?? custom?.ingredients) as Array<Record<string, unknown>> | undefined;
  const ingredients: RecipeIngredient[] = Array.isArray(customIngredients)
    ? customIngredients.map((ing, idx) => ({
        // recipeIngredientId must win; ingredientId points to ingredients catalog and 404s on /ingredients/nutrition?recipeIngredientId=
        id: (ing.recipeIngredientId as string) ?? (ing.ingredientId as string) ?? (ing.id as string) ?? (ing._id as string) ?? `ing-${ing.name}-${idx}`,
        ingredientId: (ing.ingredientId as string | undefined) ?? undefined,
        recipeIngredientId: (ing.recipeIngredientId as string | undefined) ?? undefined,
        source: (ing.source as string | undefined) ?? undefined,
        name: (ing.name as string) ?? '',
        amount: Number(ing.amount) || 0,
        unit: (ing.unit as string) ?? (ing.servingId as string) ?? '',
        originalString: `${ing.amount ?? ''} ${ing.unit ?? ing.servingId ?? ''} ${ing.name ?? ''}`.trim(),
        nutrition: (ing.nutrition as Record<string, unknown> | undefined) ?? undefined,
      }))
    : (d.ingredients as RecipeIngredient[]) ?? [];

  const nutritionDetails = flatNut && typeof flatNut === 'object'
    ? flatNutritionToRecipeNutrition(flatNut, nutritionUnits)
    : (d.nutritionDetails as RecipeNutrition) ?? (d.nutrition as RecipeNutrition) ?? undefined;
  const originalNutritionDetails = originalFlatNut && typeof originalFlatNut === 'object'
    ? flatNutritionToRecipeNutrition(originalFlatNut, originalNutritionUnits)
    : undefined;
  const originalCalorieCalculation = recipe?.calorieCalculation as RecipeCalorieCalculation | undefined;

  const originalServings = (recipe?.originalServings as number | undefined) ?? (custom?.servings as number | undefined) ?? (d.servings as number | undefined);
  const nutritionServings =
    (custom?.nutritionServings as number | undefined)
    ?? (d.nutritionServings as number | undefined);
  const servingSizes =
    normalizeServingSizes(custom?.servingSizes)
    ?? normalizeServingSizes(d.servingSizes)
    ?? normalizeServingSizes(recipe?.servingSizes);
  const defaultServingId =
    normalizeServingId(custom?.defaultServingId)
    ?? normalizeServingId(d.defaultServingId)
    ?? normalizeServingId(recipe?.defaultServingId);
  const selectedServingId =
    normalizeServingId(custom?.selectedServingId)
    ?? normalizeServingId(d.selectedServingId)
    ?? defaultServingId;
  const defaultServingFromList = servingSizes?.find((entry) => entry.servingId === defaultServingId);
  const selectedServingFromList = servingSizes?.find((entry) => entry.servingId === selectedServingId);
  const defaultServingGrams =
    toPositiveNumber(custom?.defaultServingGrams)
    ?? toPositiveNumber(d.defaultServingGrams)
    ?? toPositiveNumber(recipe?.defaultServingGrams)
    ?? defaultServingFromList?.grams;
  const selectedServingGrams =
    toPositiveNumber(custom?.selectedServingGrams)
    ?? toPositiveNumber(d.selectedServingGrams)
    ?? selectedServingFromList?.grams
    ?? defaultServingGrams;

  return {
    ...(d as unknown as RecipeDetails),
    ingredients: ingredients.length > 0 ? ingredients : (d.ingredients as RecipeIngredient[]) ?? [],
    nutritionDetails,
    originalNutritionDetails,
    originalCalorieCalculation,
    readyInMinutes: readyInMinutes ?? 0,
    totalTimeMinutes: totalTimeMinutes ?? readyInMinutes ?? 0,
    prepTimeMinutes: prepTimeMinutes ?? null,
    cookTimeMinutes: cookTimeMinutes ?? null,
    originalServings: typeof originalServings === 'number' ? originalServings : 1,
    defaultServingId,
    defaultServingGrams,
    selectedServingId,
    selectedServingGrams,
    servingSizes,
    nutritionServings:
      typeof nutritionServings === 'number' && Number.isFinite(nutritionServings) && nutritionServings > 0
        ? nutritionServings
        : undefined,
  };
}

interface RecipeDetailsModalProps {
  recipeId: string | number;
  recipeName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  draftId?: string;
  /** When viewing a finalized meal plan, use GET /recipes/customized?mealPlanId=&itemId= */
  mealPlanId?: string;
  itemId?: string;
  day?: number;
  mealName?: string;
  mealOrder?: number; // Required for automated-draft updateNutritionServings (PUT drafts/{id})
  source?: 'spoonacular' | 'edamam' | 'internal';
  onClose?: () => void;
  /** Called when user switches tabs (Ingredients / Instructions / Nutrition). Use to refetch draft so notes/ingredient changes are visible. */
  onRefetchDraft?: () => void | Promise<void>;
  /** If true, ingredient editing is restricted to quantity updates only (name/unit locked). */
  restrictIngredientEditToQuantity?: boolean;
  initialDefaultServingId?: string | null;
  initialDefaultServingGrams?: number | null;
  initialSelectedServingId?: string | null;
  initialSelectedServingGrams?: number | null;
  initialServingSizes?: RecipeServingSize[];
}

const RecipeDetailsModal: React.FC<RecipeDetailsModalProps> = ({
  recipeId,
  recipeName,
  open,
  onOpenChange,
  draftId,
  mealPlanId,
  itemId,
  day,
  mealName,
  mealOrder,
  source,
  onClose,
  onRefetchDraft,
  restrictIngredientEditToQuantity = false,
  initialDefaultServingId,
  initialDefaultServingGrams,
  initialSelectedServingId,
  initialSelectedServingGrams,
  initialServingSizes,
}) => {
  const [recipeDetails, setRecipeDetails] = useState<RecipeDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [currentServings, setCurrentServings] = useState(1);
  const [servingsInput, setServingsInput] = useState<string | null>(null);
  const [updatingServings, setUpdatingServings] = useState(false);
  const [activeTab, setActiveTab] = useState('ingredients');
  const [modalFeedback, setModalFeedback] = useState<{
    variant: 'error' | 'warning' | 'success' | 'info';
    title?: string;
    message: string;
  } | null>(null);
  const servingsDebounceTimerRef = React.useRef<NodeJS.Timeout | null>(null);
  const initialServingMetadata = React.useMemo(() => ({
    defaultServingId: initialDefaultServingId ?? null,
    defaultServingGrams: initialDefaultServingGrams ?? null,
    selectedServingId: initialSelectedServingId ?? initialDefaultServingId ?? null,
    selectedServingGrams: initialSelectedServingGrams ?? initialDefaultServingGrams ?? null,
    servingSizes: Array.isArray(initialServingSizes) && initialServingSizes.length > 0 ? initialServingSizes : undefined,
  }), [
    initialDefaultServingGrams,
    initialDefaultServingId,
    initialSelectedServingGrams,
    initialSelectedServingId,
    initialServingSizes,
  ]);
  const applyServingMetadataFallback = React.useCallback((details: RecipeDetails): RecipeDetails => ({
    ...details,
    defaultServingId: details.defaultServingId ?? initialServingMetadata.defaultServingId,
    defaultServingGrams: details.defaultServingGrams ?? initialServingMetadata.defaultServingGrams,
    selectedServingId: details.selectedServingId ?? initialServingMetadata.selectedServingId,
    selectedServingGrams: details.selectedServingGrams ?? initialServingMetadata.selectedServingGrams,
    servingSizes: Array.isArray(details.servingSizes) && details.servingSizes.length > 0
      ? details.servingSizes
      : initialServingMetadata.servingSizes,
  }), [initialServingMetadata]);

  const handleRecipeUpdate = React.useCallback((updatedRecipe: unknown) => {
    setHasChanges(true);
    if (!updatedRecipe) return;

    const data = updatedRecipe as Record<string, unknown>;
    const normalized = normalizeCustomizedRecipeResponse(data);
    const preferredNutritionDetails = resolvePreferredNutritionDetails(data);
    const customization = data.customization as Record<string, unknown> | undefined;
    const servings =
      (customization?.nutritionServings as number)
      ?? (data.nutritionServings as number)
      ?? normalized.nutritionServings
      ?? (customization?.servings as number)
      ?? (data.servings as number);

    setRecipeDetails(prevDetails => applyServingMetadataFallback({
      ...prevDetails,
      ...normalized,
      ingredients: normalized.ingredients.length > 0
        ? normalized.ingredients
        : (data.ingredients as RecipeIngredient[]) ?? prevDetails?.ingredients ?? [],
      nutritionDetails: normalized.nutritionDetails
        ?? preferredNutritionDetails
        ?? (data.nutrition as RecipeNutrition)
        ?? (data.nutritionDetails as RecipeNutrition)
        ?? prevDetails?.nutritionDetails,
      originalNutritionDetails: normalized.originalNutritionDetails ?? prevDetails?.originalNutritionDetails,
      title: (data.title as string) || (data.recipeName as string) || prevDetails?.title || '',
      image: (data.recipeImageUrl as string) || (data.image as string) || prevDetails?.image || '',
      nutritionServings: servings ?? prevDetails?.nutritionServings ?? prevDetails?.servings ?? 1,
    } as RecipeDetails));

    // Keep currentServings in sync so Nutrition header and chart update
    if (typeof servings === 'number' && !Number.isNaN(servings)) {
      setCurrentServings(servings);
      setServingsInput(null);
    }
  }, [applyServingMetadataFallback]);

  const handleNotesSaved = React.useCallback((notes: string | null) => {
    setHasChanges(true);
    setRecipeDetails(prevDetails => {
      if (!prevDetails) return prevDetails;
      return {
        ...prevDetails,
        notes,
      };
    });
  }, []);

  const handleModalClose = (isOpen: boolean) => {
    onOpenChange(isOpen);
    
    // If modal is being closed and there were changes, trigger refresh
    if (!isOpen && hasChanges && onClose) {
      onClose();
      setHasChanges(false); // Reset for next time
    }
  };

  const handleIncreaseServings = () => {
    const parsedInput = Number.parseFloat(servingsInput ?? '');
    const baseValue = Number.isFinite(parsedInput) ? parsedInput : currentServings;
    const newServings = Math.min(20, Math.round((baseValue + 0.5) * 10) / 10);
    updateServingsWithDebounce(newServings);
  };

  const handleDecreaseServings = () => {
    const parsedInput = Number.parseFloat(servingsInput ?? '');
    const baseValue = Number.isFinite(parsedInput) ? parsedInput : currentServings;
    const newServings = Math.max(0.1, Math.round((baseValue - 0.5) * 10) / 10);
    updateServingsWithDebounce(newServings);
  };

  const updateServingsWithDebounce = (newServings: number) => {
    // Update UI immediately for responsive feel
    setCurrentServings(newServings);
    setServingsInput(null);
    
    // Clear any existing timer
    if (servingsDebounceTimerRef.current) {
      clearTimeout(servingsDebounceTimerRef.current);
    }
    
    // Set new timer for API call (800ms debounce)
    servingsDebounceTimerRef.current = setTimeout(() => {
      updateServingsValue(newServings);
    }, 800);
  };

  const handleServingsInputChange = (rawValue: string) => {
    if (rawValue === '' || /^\d*\.?\d*$/.test(rawValue)) {
      setServingsInput(rawValue);
    }
  };

  const commitTypedServings = () => {
    if (servingsInput == null || servingsInput === '') return;
    const parsed = Number.parseFloat(servingsInput);
    setServingsInput(null);
    if (!Number.isFinite(parsed)) return;

    const clamped = Math.min(20, Math.max(0.1, Math.round(parsed * 10) / 10));
    updateServingsWithDebounce(clamped);
  };

  const updateServingsValue = async (newServings: number) => {
    const normalizedItemId = String(itemId ?? '').trim();
    const isFinalized = mealPlanId && itemId;
    const hasDraftContext = draftId && day !== undefined && mealName;
    const isAutomatedDraft = hasDraftContext && mealOrder && mealOrder > 0;
    const isManualDraft = hasDraftContext && (!mealOrder || mealOrder < 1);

    if (mealPlanId && !normalizedItemId) {
      setModalFeedback({
        variant: 'warning',
        title: 'Item id missing',
        message: 'Unable to update servings because item id is required.',
      });
      return;
    }

    if (!isFinalized && !hasDraftContext) {
      setCurrentServings(newServings);
      return;
    }

    try {
      setUpdatingServings(true);

      let didUpdate = false;
      if (isFinalized && mealPlanId && itemId) {
        const response = await updateFinalizedMealPlanItem(mealPlanId, {
          itemId,
          nutritionServings: newServings,
        });
        didUpdate = response.success;
      } else if (isAutomatedDraft && draftId) {
        const response = await updateNutritionServings(
          draftId,
          day ?? 0,
          mealOrder,
          String(recipeId),
          newServings,
          source
        );
        didUpdate = response.success;
      } else if (isManualDraft && draftId) {
        if (!normalizedItemId) {
          setModalFeedback({
            variant: 'warning',
            title: 'Item id missing',
            message: 'Unable to update servings because item id is required.',
          });
          return;
        }
        const response = await updateManualMealPlanServings({
          draftId,
          day: day ?? 0,
          mealName: mealName!,
          itemId: normalizedItemId,
          recipeId: String(recipeId),
          nutritionServings: newServings,
          servings: newServings,
        });
        didUpdate = response.success;
      } else {
        setModalFeedback({
          variant: 'warning',
          title: 'Unable to update servings',
          message: 'Missing recipe context for updating servings.',
        });
      }

      if (didUpdate) {
        setModalFeedback(null);
        setHasChanges(true);
        // Refetch to get updated nutritionServings and recalculated nutrition values from API
        await loadRecipeDetails(true);
      }
    } catch (error: unknown) {
      setModalFeedback({
        variant: 'error',
        title: 'Update failed',
        message: extractErrorMessage(error),
      });
    } finally {
      setUpdatingServings(false);
    }
  };

  useEffect(() => {
    if (open && recipeId) {
      setActiveTab('ingredients');
      setModalFeedback(null);
      loadRecipeDetails();
    }
  }, [open, recipeId, draftId, mealPlanId, itemId, day, mealName]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (servingsDebounceTimerRef.current) {
        clearTimeout(servingsDebounceTimerRef.current);
      }
    };
  }, []);


  const loadRecipeDetails = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      let response:
        | { success: boolean; data?: unknown; message?: string };

      if (mealPlanId) {
        const finalizedItemId = String(itemId ?? '').trim();
        if (!finalizedItemId) {
          if (!silent) {
            setModalFeedback({
              variant: 'warning',
              title: 'Item id missing',
              message: 'Unable to load recipe details because item id is required.',
            });
          }
          return;
        }
        response = await getCustomizedRecipeByItem(mealPlanId, finalizedItemId);
      } else {
        if (!draftId || day === undefined || !mealName) {
          return;
        }
        const draftItemId = String(itemId ?? '').trim();
        if (!draftItemId) {
          if (!silent) {
            setModalFeedback({
              variant: 'warning',
              title: 'Item id missing',
              message: 'Unable to load recipe details because item id is required.',
            });
          }
          return;
        }
        response = await getCustomizedRecipeDetails(String(recipeId), draftId, day, mealName, draftItemId);
      }

      if (response.success && response.data) {
        if (!silent) setModalFeedback(null);
        const normalized = normalizeCustomizedRecipeResponse(response.data);
        const payload = response.data as Record<string, unknown>;
        const preferredNutritionDetails = resolvePreferredNutritionDetails(payload);
        setRecipeDetails(applyServingMetadataFallback({
          ...normalized,
          ...(preferredNutritionDetails ? { nutritionDetails: preferredNutritionDetails } : {}),
        }));
        const customization = payload.customization as Record<string, unknown> | undefined;
        const raw = payload;
        const servingsVal =
          (customization?.nutritionServings as number | undefined)
          ?? (raw.nutritionServings as number | undefined)
          ?? normalized.nutritionServings
          ?? (customization?.servings as number | undefined)
          ?? (raw.servings as number | undefined)
          ?? normalized.servings
          ?? 1;
        setCurrentServings(typeof servingsVal === 'number' && !Number.isNaN(servingsVal) ? servingsVal : 1);
        setServingsInput(null);
      } else {
        throw new Error('Failed to load recipe details');
      }
    } catch (error) {
      if (!silent) {
        setModalFeedback({
          variant: 'error',
          title: 'Unable to load recipe details',
          message: extractErrorMessage(error),
        });
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const headerImageSrc =
    recipeDetails?.recipeImageUrl || recipeDetails?.image || DEFAULT_RECIPE_IMAGE;
  const readyMins = getReadyInMinutes(recipeDetails);
  const timeMeta = readyMins > 0 ? `${readyMins} min` : '—';
  const prepMinsRaw = recipeDetails != null ? Number(recipeDetails.prepTimeMinutes) : NaN;
  const cookMinsRaw = recipeDetails != null ? Number(recipeDetails.cookTimeMinutes) : NaN;
  const showPrepCookBreakdown =
    Number.isFinite(prepMinsRaw) &&
    prepMinsRaw > 0 &&
    Number.isFinite(cookMinsRaw) &&
    cookMinsRaw > 0;
  const weightG =
    recipeDetails && typeof recipeDetails.totalWeightG === 'number' && Number.isFinite(recipeDetails.totalWeightG) && recipeDetails.totalWeightG > 0
      ? `${Math.round(recipeDetails.totalWeightG)}g`
      : '—';
  const kcalVal = getDisplayCaloriesKcal(recipeDetails);
  const kcalMeta = kcalVal != null && Number.isFinite(kcalVal) ? `${roundNutritionDisplay(kcalVal)} kcal` : '—';

  const metaDot = (
    <span className="shrink-0 text-xs leading-[15px] tracking-[0.1172px] text-[#d1d5dc]" aria-hidden>
      ·
    </span>
  );

  return (
    <Dialog open={open} onOpenChange={handleModalClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] gap-0 overflow-y-auto p-6 [&>button]:hidden">
        {/* Header — Figma node 65:2042 */}
        <div
          className="-mx-6 -mt-6 flex items-center justify-between bg-[#f6f8fa] px-6 pb-5 pt-6"
          style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
        >
          <div className="flex min-w-0 flex-1 items-center gap-3">
            <div className="h-[46px] w-[47px] shrink-0 overflow-hidden rounded-[10px] border-[0.67px] border-solid border-[#e2e8f0] bg-white">
              <img
                src={headerImageSrc}
                alt=""
                className="h-[46px] w-full object-cover"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.onerror = null;
                  target.src = DEFAULT_RECIPE_IMAGE;
                }}
              />
            </div>
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              <DialogTitle
                className="truncate text-left text-[20px] font-bold leading-[25px] tracking-[-0.4492px] text-[#1e2939]"
                title={recipeName}
              >
                {recipeName}
              </DialogTitle>
              <div className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1">
                <span className="inline-flex h-5 shrink-0 items-center rounded-lg bg-[#f3f4f6] px-1.5 text-xs font-normal leading-[15.75px] tracking-[0.0923px] text-[#99a1af]">
                  {formatConsumptionChip(currentServings)}
                </span>
                {metaDot}
                <span className="shrink-0 text-xs font-normal leading-[15.75px] tracking-[0.0923px] text-[#99a1af]">
                  {loading ? '…' : timeMeta}
                </span>
                {metaDot}
                <span className="shrink-0 text-xs font-medium leading-[15.75px] tracking-[0.0923px] text-[#fe9a00]">
                  {loading ? '…' : kcalMeta}
                </span>
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => handleModalClose(false)}
            className="ml-3 flex size-8 shrink-0 items-center justify-center rounded-full border border-white bg-[#20aedc] text-white outline-none transition-opacity hover:opacity-90 focus-visible:ring-2 focus-visible:ring-slate-300 focus-visible:ring-offset-2"
            aria-label="Close"
          >
            <X className="size-[14px]" strokeWidth={2.25} />
          </button>
        </div>

        {modalFeedback ? (
          <div className="mt-4">
            <InlineFeedback
              variant={modalFeedback.variant}
              title={modalFeedback.title}
              message={modalFeedback.message}
            />
          </div>
        ) : null}

        {loading ? (
          <div className="flex items-center justify-center px-0 py-10">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        ) : recipeDetails ? (
          <div className="mt-5 space-y-6">
            {/* Middle section — Figma node 65:2041 (layout + styling only; same handlers / data) */}
            <div
              className="flex flex-col gap-5 pt-5"
              style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
            >
              <div className="flex flex-col gap-2 sm:flex-row sm:items-stretch">
                {/* Outlined Servings */}
                <div className="relative flex min-h-[56px] min-w-0 flex-1 flex-col justify-center rounded border border-[rgba(0,0,0,0.23)] bg-white px-3.5 pb-2 pt-4">
                  <span className="pointer-events-none absolute left-3 top-0 z-[1] max-w-[calc(100%-24px)] -translate-y-1/2 bg-white px-1 text-xs leading-none text-[rgba(0,0,0,0.6)]">
                    Servings*
                  </span>
                  <div className="flex w-full items-center justify-between gap-2 pl-0.5 pr-0.5">
                    <div className="flex min-w-0 items-center gap-1">
                      <Popover>
                        <PopoverTrigger asChild>
                          <button
                            type="button"
                            className="inline-flex shrink-0 items-center text-[rgba(0,0,0,0.45)] hover:text-[rgba(0,0,0,0.65)]"
                            aria-label="What is per consumption serving?"
                          >
                            <Info className="h-3.5 w-3.5" />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent align="start" className="w-80 text-xs leading-relaxed">
                          <p>
                            Consumption servings are the portions each person eats and the nutrition values are shown for this amount.
                          </p>
                          <p className="mt-2">
                            Ingredient servings are the total recipe yield prepared from all ingredients.
                          </p>
                          <p className="mt-2">
                            Example: a recipe cooked for 4 people has ingredient yield = 4 servings. One person&apos;s plate is 1 consumption serving, so nutrition is shown for that 1 serving.
                          </p>
                        </PopoverContent>
                      </Popover>
                      {updatingServings && (
                        <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-[rgba(0,0,0,0.45)]" />
                      )}
                    </div>
                    <div className="flex flex-1 items-center justify-center gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={handleDecreaseServings}
                        className="h-7 w-7 shrink-0 p-0 hover:bg-slate-100"
                        disabled={updatingServings || currentServings <= 0.1}
                      >
                        <Minus className="h-3.5 w-3.5" />
                      </Button>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={servingsInput ?? String(currentServings)}
                        onChange={(e) => handleServingsInputChange(e.target.value)}
                        onBlur={commitTypedServings}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            commitTypedServings();
                          }
                        }}
                        disabled={updatingServings}
                        className="h-7 w-16 border-0 bg-transparent p-0 text-center text-sm font-semibold text-[#111827] outline-none ring-0 focus-visible:ring-0 disabled:opacity-60"
                        aria-label="Servings"
                      />
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={handleIncreaseServings}
                        className="h-7 w-7 shrink-0 p-0 hover:bg-slate-100"
                        disabled={updatingServings || currentServings >= 20}
                      >
                        <Plus className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Cooking time — read-only info card (not an input; soft card matches macro tiles in this modal) */}
                <div
                  className="flex min-h-[56px] min-w-0 flex-1 flex-col justify-center gap-1.5 rounded-xl border border-[#f3f4f6] bg-white px-3.5 py-3 shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]"
                  aria-label="Cooking time from recipe (not editable)"
                >
                  <p className="text-xs font-medium uppercase tracking-[0.08em] text-[#99a1af]">
                    Cooking time
                  </p>
                  {loading ? (
                    <p className="text-sm font-semibold text-[#99a1af]">…</p>
                  ) : readyMins > 0 ? (
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-2">
                        <Clock className="h-3.5 w-3.5 shrink-0 text-[#99a1af]" strokeWidth={2} aria-hidden />
                        <span className="text-sm font-semibold tabular-nums leading-none text-[#1e2939]">
                          {readyMins} min
                        </span>
                      </div>
                      {showPrepCookBreakdown ? (
                        <p className="pl-[22px] text-xs leading-snug text-[#99a1af]">
                          Prep {Math.round(prepMinsRaw)} min
                          <span className="mx-1 text-[#d1d5dc]" aria-hidden>
                            ·
                          </span>
                          Cook {Math.round(cookMinsRaw)} min
                        </p>
                      ) : null}
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Clock className="h-3.5 w-3.5 shrink-0 text-[#99a1af]" strokeWidth={2} aria-hidden />
                      <span className="text-sm text-[#99a1af]">Not specified</span>
                    </div>
                  )}
                </div>
              </div>

              {(recipeDetails.nutrition || recipeDetails.nutritionDetails) &&
                (() => {
                  const nutrition = recipeDetails.nutritionDetails || recipeDetails.nutrition;
                  if (!nutrition) return null;

                  const macros = nutrition.macros;
                  const originalNutrition = recipeDetails.originalNutritionDetails;
                  const hasCurrentMacroRing = hasMacroDistributionNutrition(nutrition);
                  const hasOriginalMacroRing = hasMacroDistributionNutrition(originalNutrition);
                  const hasMacroComparison =
                    hasCurrentMacroRing &&
                    hasOriginalMacroRing &&
                    !areNutritionValuesEqual(nutrition, originalNutrition);

                  const caloriesOnlyCardClass =
                    'rounded-xl border border-[#f3f4f6] bg-white py-3 px-3 text-center shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]';

                  const renderNutritionTooltipContent = (isConsumptionView: boolean) => {
                    if (isConsumptionView) {
                      return (
                        <>
                          <p className="font-semibold text-[#111827]">Nutrition per Consumption</p>
                          <p>
                            This shows the actual nutrition you will get from the exact amount you&apos;re eating in this meal plan.
                          </p>
                          <p>It automatically adjusts for:</p>
                          <p>• The portion size you selected (e.g., bowl vs plate)</p>
                          <p>• How many servings of that portion you&apos;re consuming</p>
                          <p className="font-semibold text-[#111827]">Example:</p>
                          <p>
                            If the recipe makes 10 plates (1000 calories total), but you&apos;re eating 2 bowls, and 1 bowl = 1.5 plates as per weight, then this value will show the nutrition for 3 plates worth of food (2 bowls × 1.5).
                          </p>
                          <p>This is the number that matters most for your daily tracking.</p>
                        </>
                      );
                    }

                    const calculation = recipeDetails.originalCalorieCalculation;
                    if (!calculation || calculation.ingredients.length === 0) {
                      return (
                        <>
                          <p className="font-semibold text-[#111827]">Original recipe nutrition</p>
                          <p>This is the stored nutrition for one original recipe serving.</p>
                        </>
                      );
                    }

                    const adjustmentIsVisible = Math.abs(calculation.storedRecipeAdjustment) >= 0.005;
                    return (
                      <>
                        <p className="font-semibold text-[#111827]">How original calories are calculated</p>
                        <p>Each row is the ingredient&apos;s calorie contribution to the full recipe batch.</p>
                        <div className="max-h-52 space-y-1 overflow-y-auto border-y border-[#e5e7eb] py-2 pr-1">
                          {calculation.ingredients.map((ingredient, index) => {
                            const hasIngredientFormula = ingredient.amountGrams != null
                              && ingredient.caloriesPer100g != null;
                            return (
                              <div key={`${ingredient.name}-${index}`} className="space-y-0.5">
                                <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-3">
                                  <span className="truncate font-medium text-[#4b5563]">{ingredient.name}</span>
                                  <span className="font-medium tabular-nums text-[#111827]">
                                    {roundNutritionDisplay(ingredient.calories)} kcal
                                  </span>
                                </div>
                                {hasIngredientFormula ? (
                                  <p className="text-[11px] text-[#6b7280]">
                                    {roundNutritionDisplay(ingredient.amountGrams!)} g ×{' '}
                                    {roundNutritionDisplay(ingredient.caloriesPer100g!)} kcal/100 g
                                    {Math.abs(ingredient.retentionFactor - 1) >= 0.0001
                                      ? ` × retention ${roundNutritionDisplay(ingredient.retentionFactor)}`
                                      : ''}
                                  </p>
                                ) : null}
                              </div>
                            );
                          })}
                        </div>
                        <div className="space-y-1 tabular-nums">
                          <div className="flex justify-between gap-4">
                            <span>Ingredient total</span>
                            <span>{roundNutritionDisplay(calculation.ingredientCaloriesTotal)} kcal</span>
                          </div>
                          {adjustmentIsVisible ? (
                            <div className="flex justify-between gap-4 text-[#6b7280]">
                              <span>Stored recipe adjustment</span>
                              <span>
                                {calculation.storedRecipeAdjustment > 0 ? '+' : ''}
                                {roundNutritionDisplay(calculation.storedRecipeAdjustment)} kcal
                              </span>
                            </div>
                          ) : null}
                          <div className="flex justify-between gap-4 font-medium text-[#111827]">
                            <span>Batch total</span>
                            <span>{roundNutritionDisplay(calculation.batchCalories)} kcal</span>
                          </div>
                          <div className="border-t border-[#e5e7eb] pt-1 font-semibold text-[#111827]">
                            {roundNutritionDisplay(calculation.batchCalories)} ÷{' '}
                            {roundNutritionDisplay(calculation.recipeServings)} servings ={' '}
                            {roundNutritionDisplay(calculation.caloriesPerServing)} kcal per serving
                          </div>
                        </div>
                      </>
                    );
                  };

                  const renderMacroDistribution = (
                    macroNutrition: RecipeNutrition,
                    label: string,
                    helperLabel: string,
                  ) => {
                    const macroValues = macroNutrition.macros;
                    const isConsumptionView = label === 'Current nutrition';

                    return (
                      <div className="min-w-0">
                        {hasMacroComparison ? (
                          <div className="mb-3 flex min-h-6 min-w-0 items-center justify-between gap-2">
                            <TooltipProvider delayDuration={0}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <p className="min-w-0 cursor-help truncate text-[13px] font-semibold text-[#1e2939]">
                                    {label}
                                  </p>
                                </TooltipTrigger>
                                <TooltipContent side="top" align="start" className="z-[1200] max-w-[420px] space-y-2 p-3 text-xs leading-relaxed">
                                  {renderNutritionTooltipContent(isConsumptionView)}
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                            <TooltipProvider delayDuration={0}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="inline-flex shrink-0 items-center gap-1 text-xs font-medium leading-none text-[#99a1af]">
                                    {helperLabel}
                                    <Info className="h-3 w-3" />
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent side="top" align="end" className="z-[1200] max-w-[420px] space-y-2 p-3 text-xs leading-relaxed">
                                  {renderNutritionTooltipContent(isConsumptionView)}
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                        ) : null}
                        <MacroDonutWithLegend
                          contentLayout={hasMacroComparison ? 'stacked' : 'splitHalf'}
                          donutVisualSize="large"
                          carbs={convertMacroQuantityToGrams(macroValues.carbs.quantity, macroValues.carbs.unit)}
                          protein={convertMacroQuantityToGrams(macroValues.protein.quantity, macroValues.protein.unit)}
                          fat={convertMacroQuantityToGrams(macroValues.fat.quantity, macroValues.fat.unit)}
                          fiber={
                            macroValues.fiber
                              ? convertMacroQuantityToGrams(macroValues.fiber.quantity, macroValues.fiber.unit)
                              : 0
                          }
                          showFourthNutrient={false}
                          title={hasMacroComparison ? undefined : 'Macro distribution'}
                          centerPrimaryOverride={
                            macroNutrition.calories
                              ? roundNutritionDisplay(macroNutrition.calories.quantity)
                              : undefined
                          }
                          fiberLabel="Fiber"
                        />
                      </div>
                    );
                  };

                  if (hasCurrentMacroRing && macros) {
                    return (
                      <>
                        <h2 className="text-[18px] font-semibold leading-[19.5px] tracking-[-0.0762px] text-[#374151]">
                          {hasMacroComparison ? 'Nutrition comparison' : 'Nutrition (per consumption)'}
                        </h2>
                        <div className="rounded-2xl border border-[#f3f4f6] bg-white p-4 shadow-[0px_1px_3px_0px_rgba(0,0,0,0.1),0px_1px_2px_0px_rgba(0,0,0,0.1)] sm:p-5">
                          {hasMacroComparison ? (
                            <>
                              <h4 className="mb-4 text-sm font-medium text-[#111827]">
                                Macro distribution
                              </h4>
                              <div className="grid grid-cols-1 gap-0 lg:grid-cols-2">
                                <div className="min-w-0 border-b border-[#f1f5f9] pb-5 lg:border-b-0 lg:border-r lg:pb-0 lg:pr-5">
                                  {renderMacroDistribution(
                                    originalNutrition,
                                    'Original nutrition',
                                    'Analysis for recipe',
                                  )}
                                </div>
                                <div className="min-w-0 pt-5 lg:pl-5 lg:pt-0">
                                  {renderMacroDistribution(
                                    nutrition,
                                    'Current nutrition',
                                    'Analysis for consumption',
                                  )}
                                </div>
                              </div>
                            </>
                          ) : (
                            renderMacroDistribution(
                              nutrition,
                              'Current nutrition',
                              formatConsumptionChip(currentServings),
                            )
                          )}
                        </div>
                      </>
                    );
                  }

                  if (nutrition.calories) {
                    return (
                      <>
                        <h2 className="text-[18px] font-semibold leading-[19.5px] tracking-[-0.0762px] text-[#374151]">
                          Nutrition (per consumption)
                        </h2>
                        <div className="rounded-2xl border border-[#f3f4f6] bg-white p-4 shadow-[0px_1px_3px_0px_rgba(0,0,0,0.1),0px_1px_2px_0px_rgba(0,0,0,0.1)] sm:p-5">
                          <div className={caloriesOnlyCardClass}>
                            <div className="mb-0.5 text-xs text-[#6b7280]">Calories</div>
                            <div className="text-base font-semibold text-[#111827]">
                              {roundNutritionDisplay(nutrition.calories.quantity)}
                            </div>
                            <div className="mt-0.5 text-xs text-[#9ca3af]">{nutrition.calories.unit}</div>
                          </div>
                        </div>
                      </>
                    );
                  }

                  return null;
                })()}

              {recipeDetails.sourceUrl && (
                <Button
                  variant="outline"
                  className="w-full border-[#e5e7eb]"
                  onClick={() => window.open(recipeDetails.sourceUrl, '_blank')}
                >
                  <ExternalLink className="mr-2 h-4 w-4" />
                  View Original Recipe
                </Button>
              )}

              <div className="space-y-3">
                {recipeDetails.healthLabels && recipeDetails.healthLabels.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-semibold text-gray-700">Health Labels</h4>
                    <div className="flex flex-wrap gap-2">
                      {recipeDetails.healthLabels.map((label, index) => (
                        <Tag key={`health-${label}-${index}`} variant="success" size="sm">
                          {getDietaryDisplayName(label, 'healthLabels')}
                        </Tag>
                      ))}
                    </div>
                  </div>
                )}

                {((recipeDetails.dietLabels && recipeDetails.dietLabels.length > 0) || (recipeDetails.diets && recipeDetails.diets.length > 0)) && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-semibold text-gray-700">Diets</h4>
                    <div className="flex flex-wrap gap-2">
                      {(recipeDetails.dietLabels || recipeDetails.diets || []).map((diet, index) => (
                        <Tag key={`diet-${diet}-${index}`} variant="info" size="sm">
                          {getDietaryDisplayName(diet, 'dietLabels')}
                        </Tag>
                      ))}
                    </div>
                  </div>
                )}

                {((recipeDetails.cuisineTypes && recipeDetails.cuisineTypes.length > 0) || (recipeDetails.cuisines && recipeDetails.cuisines.length > 0)) && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-semibold text-gray-700">Cuisine</h4>
                    <div className="flex flex-wrap gap-2">
                      {(recipeDetails.cuisineTypes || recipeDetails.cuisines || []).map((cuisine, index) => (
                        <Tag key={`cuisine-${cuisine}-${index}`} variant="outlined" size="sm" className="capitalize">
                          {getDietaryDisplayName(cuisine, 'cuisineTypes')}
                        </Tag>
                      ))}
                    </div>
                  </div>
                )}

                {recipeDetails.cautions && recipeDetails.cautions.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-semibold text-gray-700">Cautions</h4>
                    <div className="flex flex-wrap gap-2">
                      {recipeDetails.cautions.map((caution, index) => (
                        <Tag key={`caution-${caution}-${index}`} variant="danger" size="sm">
                          {getDietaryDisplayName(caution, 'healthLabels')}
                        </Tag>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Tabs for Details */}
            <Tabs
              value={activeTab}
              onValueChange={setActiveTab}
              className="w-full"
            >
              <TabsList className="grid h-auto w-full grid-cols-3 gap-0 rounded-lg bg-[#f1f5f9] p-1.5 text-[#334155]">
                <TabsTrigger
                  value="ingredients"
                  className="gap-2 rounded-md py-2 text-sm font-medium text-[#334155] shadow-none ring-offset-0 focus-visible:ring-0 data-[state=active]:bg-white data-[state=active]:text-[#0f172a] data-[state=active]:shadow-sm data-[state=inactive]:bg-transparent"
                >
                  <ChefHat className="h-4 w-4 shrink-0" />
                  Ingredients
                </TabsTrigger>
                <TabsTrigger
                  value="instructions"
                  className="gap-2 rounded-md py-2 text-sm font-medium text-[#334155] shadow-none ring-offset-0 focus-visible:ring-0 data-[state=active]:bg-white data-[state=active]:text-[#0f172a] data-[state=active]:shadow-sm data-[state=inactive]:bg-transparent"
                >
                  <UtensilsCrossed className="h-4 w-4 shrink-0" />
                  Instructions
                </TabsTrigger>
                <TabsTrigger
                  value="nutrition"
                  className="gap-2 rounded-md py-2 text-sm font-medium text-[#334155] shadow-none ring-offset-0 focus-visible:ring-0 data-[state=active]:bg-white data-[state=active]:text-[#0f172a] data-[state=active]:shadow-sm data-[state=inactive]:bg-transparent"
                >
                  <Award className="h-4 w-4 shrink-0" />
                  Nutrition
                </TabsTrigger>
              </TabsList>

              {/* Ingredients Tab */}
              <TabsContent value="ingredients" className="mt-4">
                <IngredientsTab 
                  key={`ing-${recipeDetails?.id ?? recipeId}-${mealPlanId ?? draftId}-${itemId ?? ''}`}
                  recipeDetails={recipeDetails} 
                  draftId={draftId}
                  mealPlanId={mealPlanId}
                  itemId={itemId}
                  day={day}
                  mealName={mealName}
                  mealOrder={mealOrder}
                  recipeId={String(recipeId)}
                  source={source}
                  onRecipeUpdate={handleRecipeUpdate}
                  currentServings={currentServings}
                  restrictIngredientEditToQuantity={restrictIngredientEditToQuantity}
                />
              </TabsContent>

              {/* Instructions Tab */}
              <TabsContent value="instructions" className="mt-4">
                <InstructionsTab 
                  recipeDetails={recipeDetails}
                  planId={draftId}
                  mealPlanId={mealPlanId}
                  itemId={itemId}
                  day={day}
                  mealName={mealName}
                  mealOrder={mealOrder}
                  recipeId={recipeId}
                  onNotesSaved={handleNotesSaved}
                />
              </TabsContent>

              {/* Nutrition Tab */}
              <TabsContent value="nutrition" className="mt-4">
                <NutritionTab recipeDetails={recipeDetails} />
              </TabsContent>
            </Tabs>
          </div>
        ) : (
          <div className="mt-5 py-12 text-center text-gray-600">
            <p>No recipe details available.</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default RecipeDetailsModal;
